/**
 * BEKKA: produktkortenes ekstra billeder - hentning og mobil-gestus.
 *
 * === Hentning ===
 *
 * Billederne hentes foerst naar de skal bruges: paa computer naar kortet er
 * synligt, paa mobil foerst naar en finger lander paa kortet. Browserens egen
 * lazy-logik kan ikke bruges her. Et 'loading="lazy"'-billede uden layoutboks
 * kan ikke observeres, saa browseren henter det med det samme. Det rammer:
 *
 *   1. Forsidens faneblade er seks, hvor fem er lukkede. Maalt 21. sep 2026:
 *      132 kortbilleder hentet mod 9.
 *   2. 'grid + carousel_on_mobile' renderer kortene TO gange - et net og en
 *      mobil-dublet - og den skjulte af dem henter ogsaa sine billeder.
 *
 * Derfor staar de ekstra billeder i markuppen med en tom 1x1-GIF som src og den
 * rigtige srcset i data-srcset (se snippets/product-media.liquid). Her saettes
 * den rigtige srcset paa, naar billedet skal bruges.
 *
 * Billede 1 paa hvert kort roeres IKKE. Det er temaets eget billede med lazy og
 * sizes, og det skal blive ved med at virke uden JavaScript.
 *
 * === Mobil-gestus (Andreas' valg 21. sep 2026) ===
 *
 * Hold fingeren stille paa et kort, og det skifter til naeste billede. Slipper
 * du, er det tilbage hvor det var. Det er mobilens svar paa hover, og det er
 * bevidst at det ikke laaser sig fast: kortet er stadig et link til produktet.
 *
 * Swipe paa selve billedet haandteres af temaets egen slideshow-kode, ikke her.
 * Paa forsiden er kortene i en vandret raekke man swiper mellem produkter med,
 * og der er kortets eget swipe slaaet fra i CSS (se card-gallery.liquid), saa
 * raekken bliver ved med at virke. Dér er holdet den eneste vej til billede 2-4,
 * og derfor gaar et hold VIDERE hver gang i stedet for altid at vise billede 2.
 */

const DESKTOP = '(min-width: 750px)';
const desktop = window.matchMedia(DESKTOP);

/** Hvor laenge fingeren skal ligge stille foer billedet skifter. */
const HOLD_MS = 350;

/** Flytter fingeren sig mere end dette, er det et swipe eller en scroll. */
const FLYT_GRAENSE = 10;

/** @param {HTMLImageElement} img */
function hentBillede(img) {
  const srcset = img.dataset.srcset;
  if (!srcset) return;

  // sizes skal saettes FOER srcset, ellers vaelger browseren kandidat ud fra
  // en tom sizes (= 100vw) og henter et billede der er alt for stort.
  if (img.dataset.sizes) img.sizes = img.dataset.sizes;
  img.srcset = srcset;
  delete img.dataset.srcset;
  delete img.dataset.sizes;
}

/** @param {Element} kort */
function hentKort(kort) {
  kort.querySelectorAll('img[data-srcset]').forEach((img) => {
    if (img instanceof HTMLImageElement) hentBillede(img);
  });
}

let observer = null;

function observerKort() {
  // Paa mobil hentes der foerst naar en finger lander paa kortet. Ville vi
  // hente i forvejen, betalte hele mobiltrafikken for en gestus faa bruger.
  if (!desktop.matches) return;

  observer ||= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        hentKort(entry.target);
        observer?.unobserve(entry.target);
      }
    },
    // Lidt margen, saa billedet er der naar kortet rammer skaermkanten.
    { rootMargin: '300px' }
  );

  for (const kort of document.querySelectorAll('.card-gallery')) {
    if (kort.querySelector('img[data-srcset]')) observer.observe(kort);
  }
}

// Sikkerhedsnet: klikker nogen paa en pil, foer observeren naaede det, hentes
// billederne med det samme. Delegeret, saa det ogsaa gaelder kort der kommer til.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const pil = target.closest('slideshow-arrows, .slideshow-control');
    if (!pil) return;
    const kort = pil.closest('.card-gallery');
    if (kort) hentKort(kort);
  },
  { capture: true }
);

/* -------------------------------------------------------------------------
 * Hold fingeren = kig paa naeste billede
 * ---------------------------------------------------------------------- */

/** @type {number | null} */
let holdTimer = null;
/** Kortet fingeren ligger paa lige nu. @type {Element | null} */
let aktivtKort = null;
/** Karrusellen der skal rulles tilbage naar fingeren slippes. @type {any} */
let rulTilbage = null;
/** Indekset kortet stod paa, foer holdet flyttede det. */
let foerIndex = 0;
/** Naaede holdet at skifte billede? Styrer ogsaa om klikket skal blokeres. */
let harHoldt = false;
let startX = 0;
let startY = 0;

/**
 * Kortets egen billedkarrusel. Temaets slideshow-component er et custom
 * element, saa vi tjekker at det er opgraderet, foer vi kalder paa det.
 * @param {Element} kort
 */
function kortKarrusel(kort) {
  const slideshow = kort.querySelector('slideshow-component');
  if (!slideshow) return null;
  return typeof (/** @type {any} */ (slideshow).select) === 'function' ? slideshow : null;
}

function annullerHold() {
  if (holdTimer === null) return;
  clearTimeout(holdTimer);
  holdTimer = null;
}

/**
 * Blokerer det klik browseren ellers sender efter et hold, saa produktsiden
 * ikke aabner. Lytteren fjernes igen efter et oejeblik - i de browsere hvor
 * preventDefault paa touchend allerede har droppet klikket, maa den ikke blive
 * liggende og aede kundens naeste tryk.
 */
function blokerNaesteKlik() {
  /** @param {Event} event */
  const bloker = (event) => {
    event.preventDefault();
    event.stopPropagation();
    document.removeEventListener('click', bloker, { capture: true });
  };
  document.addEventListener('click', bloker, { capture: true });
  setTimeout(() => document.removeEventListener('click', bloker, { capture: true }), 400);
}

function slipHold() {
  annullerHold();

  const holdtLige = harHoldt;
  if (rulTilbage) rulTilbage.select(foerIndex);

  aktivtKort = null;
  rulTilbage = null;
  harHoldt = false;

  return holdtLige;
}

document.addEventListener(
  'touchstart',
  (event) => {
    if (desktop.matches) return;
    if (event.touches.length !== 1) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    const kort = target.closest('.card-gallery');
    if (!kort) return;

    // Billederne er tomme GIF'er indtil nu. Hent dem i samme oejeblik fingeren
    // lander, saa de naar frem inden skiftet 350 ms senere.
    hentKort(kort);

    const slideshow = kortKarrusel(kort);
    if (!slideshow) return;

    const touch = event.touches[0];
    if (!touch) return;

    aktivtKort = kort;
    startX = touch.clientX;
    startY = touch.clientY;
    harHoldt = false;
    rulTilbage = null;

    holdTimer = setTimeout(() => {
      holdTimer = null;

      const antal = /** @type {any} */ (slideshow).slides?.length ?? 0;
      if (antal < 2) return;

      foerIndex = /** @type {any} */ (slideshow).current ?? 0;
      rulTilbage = slideshow;
      harHoldt = true;
      /** @type {any} */ (slideshow).select((foerIndex + 1) % antal);
    }, HOLD_MS);
  },
  { passive: true, capture: true }
);

document.addEventListener(
  'touchmove',
  (event) => {
    if (!aktivtKort) return;

    const touch = event.touches[0];
    if (!touch) return;

    const flyttet =
      Math.abs(touch.clientX - startX) > FLYT_GRAENSE || Math.abs(touch.clientY - startY) > FLYT_GRAENSE;
    if (!flyttet) return;

    if (harHoldt) {
      // Fingeren er begyndt at bevaege sig EFTER at holdet skiftede billede.
      // Saa er det et swipe, og swipet skal vinde: drop tilbagerulningen, men
      // behold blokeringen af klikket, saa produktsiden ikke aabner.
      rulTilbage = null;
      return;
    }

    // Fingeren flyttede sig foer tiden gik. Det var en scroll eller et swipe,
    // ikke et hold.
    annullerHold();
    aktivtKort = null;
  },
  { passive: true, capture: true }
);

document.addEventListener(
  'touchend',
  (event) => {
    if (!aktivtKort && !harHoldt) return;

    if (slipHold()) {
      // preventDefault paa touchend fjerner det klik browseren ellers sender
      // bagefter. blokerNaesteKlik er selen paa livremmen.
      event.preventDefault();
      blokerNaesteKlik();
    }
  },
  { passive: false, capture: true }
);

document.addEventListener(
  'touchcancel',
  () => {
    if (!aktivtKort && !harHoldt) return;
    slipHold();
  },
  { capture: true }
);

/* ---------------------------------------------------------------------- */

// Nye kort kan komme til (faneblade, filtrering, "vis flere").
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches?.('.card-gallery') || node.querySelector?.('.card-gallery')) {
        observerKort();
        return;
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });

// Skifter vinduet fra mobil til computerbredde, skal observeren i gang.
desktop.addEventListener('change', observerKort);

observerKort();
